import { z } from 'zod';
import {
  currencySchema,
  paymentMethodSchema,
  type Currency,
  type SaleStatus,
  type StockMovementType,
} from './enums.js';

/** Inventory and pharmacy contracts (docs/spec.md §4.4, §7 screens 6 and 8). */

export const drugInputSchema = z.object({
  code: z.string().trim().min(1).max(32),
  /** EAN-13 in the clinic's sample data. Not every item is barcoded. */
  barcode: z.string().trim().max(64).optional().or(z.literal('')),
  nameLa: z.string().trim().min(1).max(160),
  nameEn: z.string().trim().max(160).optional().or(z.literal('')),
  unit: z.string().trim().min(1).max(32),
  category: z.string().trim().max(80).optional().or(z.literal('')),
  costPrice: z.coerce.number().int().min(0).default(0),
  sellPrice: z.coerce.number().int().min(0).default(0),
  reorderLevel: z.coerce.number().int().min(0).default(0),
  expiryDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date_format')
    .optional()
    .or(z.literal('')),
});
/** Parsed result — `.default()` fields are always present here. */
export type DrugInput = z.infer<typeof drugInputSchema>;
/**
 * What a form actually holds before parsing: the defaulted fields are optional.
 * React Hook Form needs this for its values and `DrugInput` for the submit
 * handler, so both are exported.
 */
export type DrugInputForm = z.input<typeof drugInputSchema>;

export const drugUpdateSchema = drugInputSchema.partial();
export type DrugUpdate = z.infer<typeof drugUpdateSchema>;

/** Receiving stock. Never edits `qty_on_hand` directly — it writes a movement. */
export const stockInSchema = z.object({
  qty: z.coerce.number().int().min(1).max(1_000_000),
  unitCost: z.coerce.number().int().min(0).optional(),
  note: z.string().trim().max(255).optional().or(z.literal('')),
  /** Receiving a new batch usually moves the expiry date forward. */
  expiryDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'date_format')
    .optional()
    .or(z.literal('')),
});
export type StockInInput = z.infer<typeof stockInSchema>;

/** Manual correction after a physical count. Signed: negative writes stock off. */
export const stockAdjustSchema = z.object({
  qty: z.coerce
    .number()
    .int()
    .refine((n) => n !== 0, 'nonzero_required'),
  note: z.string().trim().min(1).max(255),
});
export type StockAdjustInput = z.infer<typeof stockAdjustSchema>;

/** Categories the clinic manages in Settings. */
export const drugCategoryInputSchema = z.object({
  nameLa: z.string().trim().min(1).max(80),
  nameEn: z.string().trim().max(80).optional().or(z.literal('')),
  sortOrder: z.coerce.number().int().min(0).max(9999).default(0),
});
export type DrugCategoryInput = z.infer<typeof drugCategoryInputSchema>;
export type DrugCategoryInputForm = z.input<typeof drugCategoryInputSchema>;

export const drugCategoryUpdateSchema = drugCategoryInputSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type DrugCategoryUpdate = z.infer<typeof drugCategoryUpdateSchema>;

export interface DrugCategory {
  id: number;
  nameLa: string;
  nameEn: string | null;
  sortOrder: number;
  isActive: boolean;
  /** How many drugs currently use it — a category in use cannot be deleted. */
  drugCount: number;
}

/** One row of `stock_movements`, as shown on a drug's history view. */
export interface DrugMovement {
  id: number;
  type: StockMovementType;
  qty: number;
  note: string | null;
  refType: string | null;
  refId: number | null;
  createdAt: string;
  createdByName: string | null;
}

export type StockStatus = 'ok' | 'low' | 'out';
export type ExpiryStatus = 'ok' | 'soon' | 'expired' | 'none';

export interface Drug {
  id: number;
  code: string;
  barcode: string | null;
  nameLa: string;
  nameEn: string | null;
  unit: string;
  category: string | null;
  costPrice: number;
  sellPrice: number;
  qtyOnHand: number;
  reorderLevel: number;
  expiryDate: string | null;
  isActive: boolean;
  stockStatus: StockStatus;
  expiryStatus: ExpiryStatus;
}

export const saleItemSchema = z.object({
  drugId: z.coerce.number().int().positive(),
  qty: z.coerce.number().int().min(1).max(100_000),
  /** Overrides the catalogue price for this line, e.g. an agreed discount. */
  unitPrice: z.coerce.number().int().min(0).optional(),
});

export const saleInputSchema = z.object({
  /** Null for a walk-in customer who is not a registered patient. */
  patientId: z.coerce.number().int().positive().optional(),
  customerName: z.string().trim().max(160).optional().or(z.literal('')),
  method: paymentMethodSchema.default('cash'),
  paid: z.coerce.number().int().min(0).default(0),
  note: z.string().trim().max(2000).optional().or(z.literal('')),
  items: z.array(saleItemSchema).min(1, 'items_required'),
  /** What the customer is quoted in — the ledger total stays in kip regardless. */
  currency: currencySchema.default('LAK'),
});
export type SaleInput = z.infer<typeof saleInputSchema>;

/** Voiding a sale restores every line to stock — the reason is kept on the record. */
export const saleVoidSchema = z.object({
  reason: z.string().trim().min(1).max(255),
});
export type SaleVoidInput = z.infer<typeof saleVoidSchema>;

export interface SaleLine {
  drugId: number;
  nameLa: string;
  unit: string;
  qty: number;
  unitPrice: number;
  amount: number;
}

export interface Sale {
  id: number;
  no: string;
  patientId: number | null;
  patientName: string | null;
  customerName: string | null;
  total: number;
  paid: number;
  method: 'cash' | 'transfer' | 'card';
  note: string | null;
  status: SaleStatus;
  voidReason: string | null;
  currency: Currency;
  /** Kip per 1 unit of `currency`, snapshotted at sale time — 1 when `currency` is LAK. */
  fxRate: number;
  createdAt: string;
  createdBy: number;
  createdByName: string | null;
  items: SaleLine[];
}

/** Days before expiry at which an item starts being flagged. */
export const EXPIRY_WARN_DAYS = 90;

export function stockStatusOf(qtyOnHand: number, reorderLevel: number): StockStatus {
  if (qtyOnHand <= 0) return 'out';
  if (qtyOnHand <= reorderLevel) return 'low';
  return 'ok';
}

export function expiryStatusOf(expiryDate: string | null, today = new Date()): ExpiryStatus {
  if (!expiryDate) return 'none';
  const [y, m, d] = expiryDate.split('-').map(Number);
  if (!y || !m || !d) return 'none';
  // Compared as calendar dates, not instants — an item expiring today is not
  // "expired" only because it is now the afternoon.
  const expiry = new Date(y, m - 1, d);
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((expiry.getTime() - start.getTime()) / 86_400_000);
  if (days < 0) return 'expired';
  if (days <= EXPIRY_WARN_DAYS) return 'soon';
  return 'ok';
}
